import { type PortsAtomProps } from '@atoms/PortsAtomProps';
import { currentGraphSelector } from '@common/selectors';
import {
  GemType,
  BaseProcess,
  BaseProcessMetadata,
  BaseState,
  Connection as GraphConnection,
  GenericGraphProcess,
  GenericGraphProcessType,
  Metadata,
  CommonGraph,
  CommonState
} from '@common/types';
import { DidChangePayload } from '@common/types/messageTypes';
import { getCurrentGraphFromStore } from '@common/useOriginalGraph';
import { useLSPMethod } from '@LSP/base/hook';
import { LSP } from '@LSP/base/types';
import { useEmptyCell } from '@prophecy/ui-v3/Graph/hooks';
import { message } from '@prophecy/ui-v3/ui-compat';
import { Direction } from '@prophecy/utils/graph';
import { doubleId } from '@prophecy/utils/id';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEventState } from '@prophecy/utils/react/useEventState';
import { uniqueID } from '@prophecy/utils/uniqueID';
import { validateEntityName } from '@prophecy/utils/validation';
/**
 * Note: clone with JSON.parse(JSON.stringify(obj)) looses constructor information.
 * Due to which isEqual check between two obj can break. So we clone graph using lodash deepClone
 * which maintains constructor information added by immer.
 */
import { cloneDeep, isNil, merge } from 'lodash-es';
import { useStore } from 'react-redux';
import { XYPosition } from 'reactflow';
import { Store } from 'redux';

import { PortSchemaProps, PortType } from '../../../generic-components/PortSchema/types';
import { getConnectionPath, getProcessesPath } from '../../../Parser/bindings';
import { Port } from '../../../redux/portTypes';
import { spaceToUnderscore, uniqueIDWithDelimiter } from '../../../utils';
import { getGemSpec } from '../../getGemSpec';
import { useForwardOutputSlugs } from '../../graph/useForwardOutputSlugs';
import {
  getChangePayloadForLabelUpdate,
  getChangePayloadForPropertiesUpdate,
  isReusableSubgraph
} from '../../graph/utils';
import { uiSpecAtomPropsCacheManager } from '../../spec/utils';
import { isSqlReservedKeyword } from '../../SQL/utils';
import { getGemTypeLabelBySpec } from '../entities/entityTypes';
import { replaceNonAllowedChar } from '../string';
import { getPointsFromProcesses, snapProcessToGrid } from './layout';

import type { PropertiesDidChange } from '@LSP/base/hook';

export type AddProcessType<G extends CommonGraph = CommonGraph> = (
  gemType: GemType,
  position?: XYPosition,
  extraProps?: object,
  save?: boolean
) => GenericGraphProcessType<G>;

export function getGraphToExtractProcessLabels<G extends CommonGraph>(rootGraph: G, graph: G) {
  // if current graph is reusable subgraph use that as root
  if (isReusableSubgraph(graph)) {
    return graph;
  }
  // if rootGraph has singular test graph, use that as rootGraph
  else if ('currentSingularTestGraph' in rootGraph && typeof rootGraph.currentSingularTestGraph === 'object') {
    return rootGraph.currentSingularTestGraph as G;
  }
  // if rootGraph has snapshot graph, use that as rootGraph
  else if ('currentSnapshotGraph' in rootGraph && typeof rootGraph.currentSnapshotGraph === 'object') {
    return rootGraph.currentSnapshotGraph as G;
  }
  // if rootGraph has modelGraph, use that as rootGraph
  else if ('currentModelGraph' in rootGraph && typeof rootGraph.currentModelGraph === 'object') {
    return rootGraph.currentModelGraph as G;
  }
  // if rootGraph has pipeline graph, use that as rootGraph
  else if ('currentPipelineGraph' in rootGraph && typeof rootGraph.currentPipelineGraph === 'object') {
    return rootGraph.currentPipelineGraph as G;
  }

  return rootGraph;
}
export function getAllProcessLabels(graph: CommonGraph, list: string[] = []) {
  const processes = Object.values(graph.processes);
  for (let index = 0; index < processes.length; index++) {
    const process = processes[index] as CommonGraph;
    list.push(process.metadata.label);
    //Is rename will handle the case for published or reusable subgraph in same project
    if (Object.keys(process.processes || {}).length > 0 && !isReusableSubgraph(process)) {
      getAllProcessLabels(process, list);
    }
  }
  return list;
}

/**
 * Returns a unique label for a process in the given graph (e.g. for SQL node names).
 * Avoids reserved SQL keywords and existing labels.
 */
export function getUniqueProcessLabel(label: string, graph: CommonGraph): string {
  const allProcessLabels = getAllProcessLabels(graph);
  if (isSqlReservedKeyword(label)) {
    label = `${label}_cte`;
  }
  return uniqueIDWithDelimiter(replaceNonAllowedChar(label), allProcessLabels);
}

export function useModifyPropertiesAttribute(currentGraphPath: string, saveAfterChange?: boolean) {
  const { method: propertiesDidChange } = useLSPMethod(LSP.Method.propertiesDidChange);
  const { method: propertiesDidSave } = useLSPMethod(LSP.Method.propertiesDidSave);

  return usePersistentCallback(function onModifyPropertiesAttribute<
    P extends GenericGraphProcess<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, GraphConnection>
  >(property: string, value: unknown, process: P, graphPath = currentGraphPath) {
    const payload = getChangePayloadForPropertiesUpdate(property, value, process.id, graphPath);
    propertiesDidChange(payload);
    if (saveAfterChange) propertiesDidSave();
  });
}
export function useRenameProcess<G extends CommonGraph>(
  getGraphs: () => { rootGraph: G; graph: G },
  propertiesDidChange: PropertiesDidChange,
  currentGraphPath: string,
  forwardSlug?: boolean
) {
  const forwardOutputSlugs = useForwardOutputSlugs();

  return usePersistentCallback(function onRenameProcess<
    P extends GenericGraphProcess<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, GraphConnection>
  >(
    value: string,
    process: P,
    graphPath = currentGraphPath,
    validateName: (name: string) => string | undefined = validateEntityName
  ) {
    const { rootGraph, graph } = getGraphs();
    let result = false;
    const validateResult = validateName(value);
    if (validateResult) {
      message.error({ content: validateResult });
    } else if (
      // for reusable subgraph we limit the name to that subgraph scope. So no need to lookup from parent.
      getAllProcessLabels(getGraphToExtractProcessLabels(rootGraph, graph), [])
        .filter((label) => process.metadata.label !== label)
        .includes(value)
    ) {
      message.error({ content: `Label is already in use.` });
    } else {
      const payload = getChangePayloadForLabelUpdate(value, process, graphPath);
      propertiesDidChange(payload);

      const outPorts = process.ports?.outputs;
      // forward the process label as input slug for the next component only if there is single output port, in other case
      // we forward output port slug
      if (forwardSlug && outPorts && outPorts.length === 1) {
        const updatedProcess = { [process.id]: { ...process, metadata: payload.value } };
        const connections = graph.connections.filter((connection) => connection.source === process.id);

        forwardOutputSlugs(graph, currentGraphPath, connections, updatedProcess);
      }

      result = true;
    }
    return result;
  });
}
export function useAddPartialGraph<G extends CommonGraph = CommonGraph>(graphPath: string, forwardSlug?: boolean) {
  const { method: propertiesDidChange } = useLSPMethod(LSP.Method.propertiesDidChange);
  const { method: propertiesDidSave } = useLSPMethod(LSP.Method.propertiesDidSave);
  const forwardOutputSlugs = useForwardOutputSlugs();
  const getGraph = useEventState<CommonState, CommonGraph>((state) => {
    const { graph } = currentGraphSelector(state);
    return graph;
  });
  return (partialGraph: G) => {
    const { processes, connections } = cloneDeep(partialGraph);

    const processList = Object.values(processes);

    if (!processList.length) return;

    /**
     * TODO: backend has bug where if updated port is sent along with the new process,
     * it doesn't update the properties, so we send the update input ports as separate didChange
     * Once fixed remove this logic
     */
    const processInputPorts: { inputPorts: Port[]; processId: string }[] = [];

    propertiesDidChange([
      ...processList.map((process) => {
        if (process.ports?.inputs?.length) {
          processInputPorts.push({ inputPorts: process.ports.inputs, processId: process.id });
          process.ports.inputs = process.ports.inputs.map((port, index) => ({
            ...port,
            slug: `${PortType.input}${index}`
          }));
        }

        return {
          property: `${getProcessesPath(graphPath)}.${process.id}`,
          value: snapProcessToGrid(process)
        };
      }),
      ...processInputPorts.map(({ processId, inputPorts }) => {
        return {
          property: `${getProcessesPath(graphPath)}.${processId}.ports.inputs`,
          value: inputPorts
        };
      })
    ]);
    const graph = getGraph();
    if (forwardSlug) {
      forwardOutputSlugs(graph, graphPath, connections, processes);
    }

    propertiesDidChange({
      property: getConnectionPath(graphPath),
      value: [...graph.connections, ...connections]
    });

    propertiesDidSave();
  };
}
export function useAddNewProcess<G extends CommonGraph = CommonGraph, S = BaseState<G, Metadata>>(
  gemSpecs: BaseState<G, Metadata>['gemSpecs'],
  getGraphs: (state: S) => { rootGraph: G; graph: G; graphPath: string },
  prepareProcess?: (process: GenericGraphProcessType<G>) => GenericGraphProcessType<G>,
  force = false
) {
  const store = useStore<S>();
  const getEmptyCell = useEmptyCell();
  const { method: propertiesDidChange } = useLSPMethod(LSP.Method.propertiesDidChange, {
    readonly: force ? false : undefined
  });
  const { method: propertiesDidSave } = useLSPMethod(LSP.Method.propertiesDidSave, {
    readonly: force ? false : undefined
  });

  const _createProcess = usePersistentCallback(function _createProcess(gemType: GemType, extraProps?: object) {
    const { rootGraph, graph } = getGraphs(store.getState());
    return createProcess(gemSpecs, rootGraph, graph, gemType, extraProps);
  });

  const dropProcess = usePersistentCallback(function dropProcess(
    process: GenericGraphProcessType<G>,
    position?: XYPosition,
    direction?: Direction
  ) {
    let x: number, y: number;

    if (position) {
      ({ x, y } = position);
    } else {
      const { graph } = getGraphs(store.getState());
      [x, y] = getEmptyCell(getPointsFromProcesses(Object.values(graph.processes)), direction);
    }

    process.metadata.x = x;
    process.metadata.y = y;
    return snapProcessToGrid(process);
  });

  const saveProcess = usePersistentCallback(function saveProcess(process: GenericGraphProcessType<G>) {
    const { graphPath } = getGraphs(store.getState());
    propertiesDidChange({
      property: `${getProcessesPath(graphPath)}.${process.id}`,
      value: process
    });
    return propertiesDidSave();
  });

  const addProcess: AddProcessType<G> = usePersistentCallback(function addProcess(
    gemType: GemType,
    position?: XYPosition,
    extraProps?: object,
    save?: boolean,
    direction?: Direction
  ) {
    const _save = save ?? true;

    let newProcess = _createProcess(gemType, extraProps);

    dropProcess(newProcess, position, direction);

    // modify the process before saving
    newProcess = prepareProcess ? prepareProcess(newProcess) : newProcess;

    if (_save) {
      saveProcess(newProcess);
    }
    return newProcess;
  });

  return { addProcess, createProcess: _createProcess, dropProcess, saveProcess };
}

export function createProcess<G extends CommonGraph = CommonGraph>(
  gemSpecs: BaseState<G, Metadata>['gemSpecs'],
  rootGraph: G,
  graph: G,
  gemType: GemType,
  extraProps?: object
) {
  const spec = getGemSpec(gemSpecs, gemType);
  let newProcess = cloneDeep(
    spec?.defaultState ?? { metadata: {}, properties: {}, ports: { outputs: [], inputs: [] } }
  ) as GenericGraphProcessType<G>;

  const gemTypeLabel = spec ? getGemTypeLabelBySpec(spec) : gemType.component;
  const name = uniqueID(`${gemTypeLabel}_`, getAllProcessLabels(getGraphToExtractProcessLabels(rootGraph, graph), []));
  newProcess.id = doubleId();
  newProcess.component = gemType.component;
  newProcess.componentInfo = gemType.componentInfo;
  newProcess.category = gemType.category;
  newProcess.metadata.label = name;
  newProcess.metadata.slug = spaceToUnderscore(name);

  newProcess.ports?.inputs?.forEach((p) => {
    p.id = doubleId();
  });
  newProcess.ports?.outputs?.forEach((p) => {
    p.id = doubleId();
  });

  if (extraProps) {
    newProcess = merge(newProcess, extraProps);
  }

  // this is a hack as backend cannot send the default state for
  // gem based on the ui spec as it is too complicated
  const portsAtomProps =
    uiSpecAtomPropsCacheManager.getAtomProps<PortsAtomProps>('Ports', gemSpecs, newProcess, gemType.component) ||
    uiSpecAtomPropsCacheManager.getAtomProps<PortSchemaProps>('PortSchema', gemSpecs, newProcess, gemType.component);
  if (portsAtomProps && newProcess.ports) {
    // script component has `isCustomOutputSchema` true by default and we need
    // to respect it until it is fixed in the backend
    newProcess.ports.isCustomOutputSchema =
      newProcess.ports.isCustomOutputSchema || portsAtomProps.defaultCustomOutputSchema;
  }

  return newProcess;
}

const BULK_PROCESSES_CHANGE_THRESHOLD = 5;

export type GraphProcessUpdates = {
  [processId: string]: Partial<CommonGraph['processes'][string]> | null;
};

export function useGroupedProcessDidChange(
  store: Store<CommonState>,
  threshold: number = BULK_PROCESSES_CHANGE_THRESHOLD
) {
  const { method: propertiesDidChange } = useLSPMethod(LSP.Method.propertiesDidChange);

  return usePersistentCallback((changes: GraphProcessUpdates, historyMergeId?: string) => {
    const payload: DidChangePayload = [];

    const { graphPath, graph } = getCurrentGraphFromStore(store);

    if (Object.keys(changes).length > threshold) {
      const updatedProcesses = {
        ...graph.processes
      };

      Object.entries(changes).forEach(([processId, change]) => {
        if (!(processId in graph.processes)) {
          // new process that needs to be created
          updatedProcesses[processId] = change as CommonGraph['processes'][string];
          return;
        }

        if (isNil(change)) {
          delete updatedProcesses[processId];
          return;
        }

        updatedProcesses[processId] = {
          ...graph.processes[processId],
          ...change
        };
      });

      payload.push({ property: getProcessesPath(graphPath), value: updatedProcesses });
    } else {
      Object.entries(changes).forEach(([processId, change]) => {
        const updatedProcess = isNil(change)
          ? null
          : {
              ...graph.processes[processId],
              ...change
            };

        payload.push({ property: `${getProcessesPath(graphPath)}.${processId}`, value: updatedProcess });
      });
    }

    propertiesDidChange(payload, historyMergeId ? { historyMergeId } : undefined);
  });
}
